import { useEffect, useRef, useState } from "react";
import { IoAdd } from "react-icons/io5";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";

import WOService from "src/Services/API/wo.service";
import UserService from "src/Services/API/user.service";
import { handleError } from "src/Common/function";
import {
  Dropzone,
  FileTypeIcon,
  GhostButton,
  ModalShell,
  PrimaryButton,
  TrashIcon,
  compressIfNeeded,
  formatBytes,
  isAcceptedFile,
} from "./uploadModalUI";

let itemIdCounter = 0;
const nextItemId = () => `file-${Date.now()}-${itemIdCounter++}`;

const isImageFile = (file) =>
  file?.type?.startsWith("image/") || /\.(png|jpe?g|gif|bmp|webp|svg|heic|heif|tiff?)$/i.test(file?.name || "");

const buildItem = (file) => ({
  id: nextItemId(),
  file,
  // PDFs render the shared blue file icon instead of a thumbnail.
  previewUrl: isImageFile(file) ? URL.createObjectURL(file) : null,
});

/**
 * Simplified upload modal for flows without per-file metadata (proposals and
 * invoices). Same shell/dropzone as the photo/document modals, but the staged
 * list is just file rows (thumbnail, name, size, remove) plus "Add More" and
 * "Upload All (N)".
 *
 * `toastNoun` names the entity in the success toast ("proposal", ...).
 */
const UploadFilesModal = ({
  shown,
  order,
  uploadFlow,
  title,
  accept,
  footer,
  toastNoun,
  onClose,
  onUploaded,
}) => {
  const currentUser = UserService.getCurrentUser();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  // Mirror of `items` so the unmount cleanup can revoke the latest preview URLs.
  const itemsRef = useRef([]);
  itemsRef.current = items;

  // Reset whenever the modal (re)opens.
  useEffect(() => {
    if (!shown) return;
    itemsRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
    setItems([]);
    setUploading(false);
  }, [shown]);

  // Revoke any outstanding preview URLs on unmount to avoid leaks.
  useEffect(
    () => () => itemsRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl)),
    [],
  );

  if (!shown) return null;

  const addFiles = (files) => {
    const accepted = files.filter((file) => isAcceptedFile(file, accept));
    if (accepted.length < files.length) toast(`Unsupported file type. ${footer}`);
    if (!accepted.length) return;
    setItems((prev) => [...prev, ...accepted.map(buildItem)]);
  };

  const removeItem = (id) =>
    setItems((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });

  const handleUpload = async () => {
    if (!items.length || !order?.id) return;
    setUploading(true);
    try {
      const files = [];
      for (const item of items) {
        files.push(await compressIfNeeded(item.file, imageCompression));
      }
      const res = await WOService.uploadFiles(files, order.id, uploadFlow, currentUser);
      if (res.status === 201) {
        toast(`${files.length} ${toastNoun}${files.length > 1 ? "s" : ""} uploaded successfully`);
        onUploaded?.();
        onClose?.();
      } else {
        toast("Something went wrong");
      }
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
    }
  };

  // ---- Empty state: dropzone ----
  if (!items.length) {
    return (
      <ModalShell
        title={title}
        width="w-[500px]"
        onClose={onClose}
        footer={<GhostButton onClick={onClose}>Cancel</GhostButton>}
      >
        <Dropzone
          accept={accept}
          multiple
          title={title}
          hint="Drag & Drop files here"
          footer={footer}
          onFiles={addFiles}
          onReject={(message) => toast(message)}
        />
      </ModalShell>
    );
  }

  // ---- Staged file list ----
  return (
    <ModalShell
      title={title}
      badge={`${items.length} File${items.length > 1 ? "s" : ""}`}
      width="w-[500px]"
      onClose={onClose}
      footer={
        <>
          <label className="h-[40px] px-1 flex items-center gap-2 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#0065FD] cursor-pointer mr-auto">
            <IoAdd size={18} />
            Add More
            <input
              type="file"
              accept={accept}
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) addFiles(files);
                if (e.target) e.target.value = null;
              }}
            />
          </label>
          <GhostButton onClick={onClose} disabled={uploading}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleUpload} loading={uploading}>
            Upload All ({items.length})
          </PrimaryButton>
        </>
      }
    >
      <div className="p-6 flex flex-col gap-3">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-3 px-4 py-3 rounded-[12px] border border-[#E5E8F3] bg-[#F8F9FE]"
          >
            {item.previewUrl ? (
              <div className="w-[40px] h-[40px] shrink-0 rounded-[6.67px] overflow-hidden bg-[#DBEAFE]">
                <img src={item.previewUrl} alt={item.file.name} className="w-full h-full object-cover" />
              </div>
            ) : (
              <FileTypeIcon />
            )}
            <div className="flex-1 min-w-0 flex flex-col">
              <span
                className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332] truncate"
                title={item.file.name}
              >
                {item.file.name}
              </span>
              <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
                {formatBytes(item.file.size)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => removeItem(item.id)}
              className="p-2 text-[#7C829A] cursor-pointer hover:bg-slate-100 rounded-[6px]"
              aria-label="Remove file"
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>
    </ModalShell>
  );
};

export default UploadFilesModal;
